import {
  OFFER_SINGLE_OPTION_DISCOUNT_ENTRY_TYPE,
  OFFER_SINGLE_OPTION_DISCOUNT_TYPE,
  OfferSingleOptionProduct
} from '@ag1/atlas';
import { useLocale } from '@portal/shared/hooks/useLocale';
import { useShopifyCartUrl } from '@portal/shared/hooks/useShopifyCartUrl';
import { useStoreValue } from '@portal/state/hooks/useStore';
import { useCallback } from 'react';
import { CHECKOUT_TYPE } from '@portal/state/cart/constants';
import { ShopifyCartAttribute, ShopifyCartItem } from '@portal/types/shopify/cart.types';
import { datadogRum } from '@datadog/browser-rum';
import { MONITORING_TAGS } from '@portal/constants/monitoring';
import { createShopifyCart } from '@portal/api/shopifyV2/cart';
import { getFormattedShopifyId } from 'shared/utils/shopify/ids';
import {
  getSellingPlanId,
  getSellingPlanIdForStoreCode,
  isSupportedCheckoutFlowType,
  useLocaleForAtlas
} from '@portal/shared/utils/offer/generic';
import { PortalOffer, ShopifyVariantsByStoreCode } from '@portal/types/offer';
import { useMutationOfferCreateBasicDiscount } from '@portal/state/internal/offerCreateBasicDiscount';
import { useMutationAddDiscountCodesToCart } from '@portal/state/internal/offerDiscountRedeemCode';
import { CreateBasicDiscountResponse } from '@portal/state/internal/offerCreateBasicDiscount/types';
import { getShopifyStringId } from '@portal/shared/utils/shopify/getShopifyStringId';
import { CheckoutFlow } from '../types';
import { isFreeShopifyVariant } from '../utils';
import { SubscriptionSelectionBaBEvent } from 'analytics/events/studio';

const { WholeOrder, PerProduct } = OFFER_SINGLE_OPTION_DISCOUNT_TYPE;
const { Value, Percentage } = OFFER_SINGLE_OPTION_DISCOUNT_ENTRY_TYPE;

export const useCheckoutHooks = () => {
  const store = useStoreValue();
  const locale = useLocale();
  const atlasLocale = useLocaleForAtlas();

  const createBasicDiscountMutation = useMutationOfferCreateBasicDiscount();
  const discountRedeemCodeMutation = useMutationAddDiscountCodesToCart();

  const { getShopifyCartUrl } = useShopifyCartUrl();

  // fetch url for multiple options offers on offer section CTA to reach
  // BaB step 1
  const parseBaBUrl = useCallback(
    (value: CheckoutFlow, offerId: number): `/${string}${string}` | null => {
      const { type } = value;
      if (isSupportedCheckoutFlowType(type)) {
        return `/offer/${type}/${offerId}`;
      }
      return null;
    },
    []
  );

  // checkout logic for single option offers on offer section CTA to reach
  // shopify checkout page
  const parseDirectCheckoutUrl = useCallback(
    async (
      offer: PortalOffer,
      singleOptionProducts: OfferSingleOptionProduct[] | null | undefined,
      shopifyVariantsByStoreCode: ShopifyVariantsByStoreCode | null | undefined
    ) => {
      if (!singleOptionProducts || !shopifyVariantsByStoreCode) return '';

      let checkoutUrl;

      try {
        const cartInput = {
          lines: [] as ShopifyCartItem[],
          attributes: [
            { key: 'checkoutOriginUrl', value: window.location.href },
            { key: CHECKOUT_TYPE.key, value: CHECKOUT_TYPE.EXPRESS_CHECKOUT }
          ]
        };

        singleOptionProducts.forEach((product) => {
          const productData = product.product_data;

          if (!productData) return;

          // DO NOT REMOVE: selling plan ID is needed for recurring products
          // for member subscription app to pick up that this is a displayable
          // subscription in their member account
          const attributes: ShopifyCartAttribute[] = [];

          let subscriptionEventType: 'single' | 'double' | 'OTP' | undefined;
          if ('is_onetime_purchase' in product && product.is_onetime_purchase) {
            subscriptionEventType = 'OTP';
          } else if (product.name.includes('Single ')) {
            subscriptionEventType = 'single';
          } else if (product.name.includes('Double ')) {
            subscriptionEventType = 'double';
          }

          SubscriptionSelectionBaBEvent({
            store,
            clickText: 'Checkout',
            subscriptionType: subscriptionEventType
          });

          let defaultStoreSellingPlanId = undefined;

          if (
            ('is_onetime_purchase' in product && !product?.is_onetime_purchase) ||
            ('is_first_order_only' in product && !product?.is_first_order_only)
          ) {
            defaultStoreSellingPlanId = getSellingPlanIdForStoreCode(store);
          }

          const sellingPlanId = getSellingPlanId(
            productData,
            // TODO(OfferBuilder-v2) Customizable selling plan ID (should be the
            // subscription frequency)
            defaultStoreSellingPlanId
          );

          if (sellingPlanId) {
            attributes.push({
              key: '__shop_subscriptions',
              value: 'true'
            });
          }

          const isWelcomeGift = product.name.toLowerCase().includes('welcome kit');

          if (isWelcomeGift) {
            attributes.push({
              // double underscore makes the custom attribute
              // hidden on the checkout
              key:
                subscriptionEventType === 'OTP'
                  ? '__custom.otp_welcome_kit'
                  : '__custom.welcome_kit',
              value: 'true'
            });
          }

          cartInput.lines.push({
            merchandiseId: getFormattedShopifyId(
              'ProductVariant',
              productData.shopify_product_variant_uri
            ),
            quantity: product.quantity,
            ...(sellingPlanId && { sellingPlanId }),
            ...(attributes.length && { attributes })
          });
        });

        let cart = await createShopifyCart(store, cartInput);

        if (!cart?.id) {
          throw new Error('Failed to create Shopify cart.');
        }

        const discountType = offer?.single_option_discount_type?.[atlasLocale];
        const discountConfig = offer?.single_option_discount_config?.[atlasLocale];

        if (discountType && discountConfig) {
          if (discountType === WholeOrder) {
            const discountEntryType = discountConfig[0]?.type;
            const discountEntryAmount = discountConfig[0]?.amount;

            if (discountEntryType && discountEntryAmount) {
              const uniqueDiscountCodeIdentifier = crypto.randomUUID().slice(0, 6);
              const discountCodeResponse = await createBasicDiscountMutation.mutateAsync({
                discountInput: {
                  title: `SINGLE-OPTION-WHOLE_ORDER-DISCOUNT-${uniqueDiscountCodeIdentifier} (OFFER-ID: ${offer.id})`,
                  code: `OFFER-${uniqueDiscountCodeIdentifier}-${offer.id}`,
                  startsAt: new Date(),
                  appliesOncePerCustomer: true,
                  customerSelection: {
                    all: true
                  },
                  combinesWith: {
                    productDiscounts: true,
                    shippingDiscounts: true
                  },
                  customerGets: {
                    items: {
                      all: true
                    },
                    value: {
                      ...(discountEntryType === Value && {
                        discountAmount: {
                          amount: discountEntryAmount,
                          appliesOnEachItem: false
                        }
                      }),
                      ...(discountEntryType === Percentage && {
                        percentage: discountEntryAmount / 100
                      })
                    },
                    appliesOnOneTimePurchase: true,
                    appliesOnSubscription: true
                  },
                  usageLimit: 1
                }
              });

              cart = await discountRedeemCodeMutation.mutateAsync({
                input: {
                  cartId: cart.id,
                  discountCodes: [discountCodeResponse.discountCode]
                }
              });
            }
          } else if (discountType === PerProduct) {
            const allDiscountsPromises: Promise<CreateBasicDiscountResponse>[] = [];
            const uniqueDiscountCodeIdentifier = crypto.randomUUID().slice(0, 6);

            discountConfig.forEach(async (entry, index) => {
              const product = singleOptionProducts.find(
                (x) => x.offer_product_type_id === entry.product_type_id
              );
              const shopifyVariantData = shopifyVariantsByStoreCode[store]?.find(
                (y) => y.id === product?.product_data.shopify_product_variant_uri
              );

              if (product && shopifyVariantData) {
                const discountEntryType = entry?.type;
                const discountEntryAmount = entry?.amount;
                const isFreeProduct = isFreeShopifyVariant(shopifyVariantData);

                if (discountEntryType && discountEntryAmount && !isFreeProduct) {
                  const shortVariantId = getShopifyStringId(
                    product.product_data.shopify_product_variant_uri
                  );

                  let appliesOnOneTimePurchase = false;
                  let appliesOnSubscription = false;

                  if (
                    ('is_onetime_purchase' in product && product.is_onetime_purchase) ||
                    ('is_first_order_only' in product && product.is_first_order_only)
                  ) {
                    appliesOnOneTimePurchase = true;
                  }

                  if (
                    ('is_onetime_purchase' in product && !product.is_onetime_purchase) ||
                    ('is_first_order_only' in product && !product.is_first_order_only)
                  ) {
                    appliesOnSubscription = true;
                  }

                  allDiscountsPromises.push(
                    createBasicDiscountMutation.mutateAsync({
                      discountInput: {
                        title: `SINGLE-OPTION-PER_PRODUCT-DISCOUNT-${uniqueDiscountCodeIdentifier}-${
                          index + 1
                        } (OFFER-ID: ${offer.id}) (PRODUCT-VARIANT-ID: ${shortVariantId})`,
                        code: `OFFER-${uniqueDiscountCodeIdentifier}-${index + 1}-${
                          offer.id
                        }-${shortVariantId}`,
                        startsAt: new Date(),
                        appliesOncePerCustomer: true,
                        customerSelection: {
                          all: true
                        },
                        combinesWith: {
                          productDiscounts: true,
                          shippingDiscounts: true
                        },
                        customerGets: {
                          items: {
                            all: false,
                            products: {
                              productVariantsToAdd: [
                                product.product_data.shopify_product_variant_uri
                              ]
                            }
                          },
                          value: {
                            ...(discountEntryType === Value && {
                              discountAmount: {
                                amount: discountEntryAmount,
                                appliesOnEachItem: false
                              }
                            }),
                            ...(discountEntryType === Percentage && {
                              percentage: discountEntryAmount / 100
                            })
                          },
                          appliesOnOneTimePurchase,
                          appliesOnSubscription
                        },
                        usageLimit: 1
                      }
                    })
                  );
                }
              }
            });

            const allDiscountsResponse = await Promise.all(allDiscountsPromises);

            const allDiscountCodes = allDiscountsResponse.map((x) => x.discountCode);

            cart = await discountRedeemCodeMutation.mutateAsync({
              input: {
                cartId: cart.id,
                discountCodes: allDiscountCodes
              }
            });
          }
        }

        checkoutUrl = await getShopifyCartUrl(store, cart.id, locale);

        if (!checkoutUrl) {
          throw new Error('Failed to generate checkout URL.');
        }
      } catch (err) {
        const tags = {
          [MONITORING_TAGS.MARKETING]: true,
          [MONITORING_TAGS.CHECKOUT]: true
        };
        datadogRum.addError(err, { tags });
      }

      return checkoutUrl;
    },
    [
      store,
      locale,
      getShopifyCartUrl,
      atlasLocale,
      createBasicDiscountMutation,
      discountRedeemCodeMutation
    ]
  );

  return {
    parseBaBUrl,
    parseDirectCheckoutUrl
  };
};
